import React, { useEffect, useState } from "react";
import { Mail, CalendarClock, Users, ArrowRight, Briefcase } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [recentMessages, setRecentMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // Store all for counting
  const [recentBookings, setRecentBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);

  useEffect(() => {
    fetchAllMessages();
    fetchRecentBookings();
  }, []);

  const fetchAllMessages = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/contact/admin`);
      const data = await res.json();
      setAllMessages(data || []);
      setRecentMessages(data ? data.slice(0, 5) : []);
    } catch (err) {
      console.error("Error fetching messages");
    }
  };

  const fetchRecentBookings = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(`${API_BASE}/api/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setRecentBookings(data.bookings.slice(0, 5));
        // Store all bookings to calculate stats (in a real app, use a stats endpoint)
        setAllBookings(data.bookings);
      }
    } catch (err) {
      console.error("Error fetching bookings");
    }
  };

  // Calculate stats
  // Robust check: If it has a date & time, it's a Slot/1:1 Booking.
  const isOneToOne = (b) => (b.date && b.time) ||
    b.session_type === 'Counselling' ||
    b.session_type === 'one_to_one' ||
    (b.session_type && (b.session_type.includes('1:1') || b.session_type.includes('1 : 1')));

  const programBookingsCount = allBookings.filter(b => !isOneToOne(b)).length;

  // Calculate Message Counts
  const generalUnread = allMessages.filter(m => !m.is_read && !m.subject.trim().startsWith('[PROPOSAL]')).length;
  const corporateUnread = allMessages.filter(m => !m.is_read && m.subject.includes('Corporate Training')).length;
  const groupUnread = allMessages.filter(m => !m.is_read && m.subject.includes('Group Training')).length;
  const legacyUnread = allMessages.filter(m => !m.is_read && m.subject.includes('Legacy Growth Mastery')).length;
  const leadershipUnread = allMessages.filter(m => !m.is_read && m.subject.includes('Leadership Legacy Program')).length;

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold">Admin Dashboard</h1>

      {/* -------- STATS -------- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={<Mail />}
          label="Contact Messages"
          value={generalUnread > 0 ? `${generalUnread} Unread` : "Check"}
          onClick={() => navigate("/admin/contact-messages")}
        />

        <StatCard
          icon={<Users />}
          label="1:1 Bookings"
          value={`${allBookings.filter(b => isOneToOne(b)).length} Bookings`}
          onClick={() => navigate("/admin/one-to-one")}
        />

        <StatCard
          icon={<Briefcase />}
          label="Corporate Training"
          value={corporateUnread > 0 ? `${corporateUnread} Unread` : "View"}
          onClick={() => navigate("/admin/corporate-proposals")}
          className="bg-blue-50 border-blue-200"
        />

        <StatCard
          icon={<Users />}
          label="Group Training"
          value={groupUnread > 0 ? `${groupUnread} Unread` : "View"}
          onClick={() => navigate("/admin/group-proposals")}
          className="bg-green-50 border-green-200"
        />

        <StatCard
          icon={<Users />}
          label="Legacy Growth Mastery"
          value={legacyUnread > 0 ? `${legacyUnread} Unread` : "View"}
          onClick={() => navigate("/admin/legacy-proposals")}
          className="bg-purple-50 border-purple-200"
        />

        <StatCard
          icon={<Users />}
          label="Leadership Legacy Program"
          value={leadershipUnread > 0 ? `${leadershipUnread} Unread` : "View"}
          onClick={() => navigate("/admin/leadership-legacy-proposals")}
          className="bg-purple-50 border-purple-200"
        />

        <StatCard
          icon={<CalendarClock />}
          label="Slot Management"
          value="Manage"
          onClick={() => navigate("/admin/slots")}
        />

        <StatCard
          icon={<Users />}
          label="Course Enrollments"
          value={`${programBookingsCount} Enrolled`}
          onClick={() => navigate("/admin/course-enrollments")}
          className="bg-purple-50 border-purple-200"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* -------- RECENT MESSAGES -------- */}
        <div className="bg-white border rounded-xl p-6 shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg">Recent Messages</h2>
            <button
              onClick={() => navigate("/admin/contact-messages")}
              className="text-sm text-exiser-primary font-semibold flex items-center gap-1"
            >
              View All <ArrowRight size={14} />
            </button>
          </div>

          {recentMessages.length === 0 ? (
            <p className="text-gray-500 text-sm">No messages</p>
          ) : (
            <ul className="space-y-3">
              {recentMessages.map((m) => (
                <li
                  key={m.id}
                  className="border p-3 rounded-lg flex justify-between items-center"
                >
                  <div>
                    <p className="font-medium">{m.name}</p>
                    <p className="text-xs text-gray-500 truncate max-w-xs">
                      {m.subject}
                    </p>
                  </div>
                  {!m.is_read && (
                    <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full font-bold">
                      New
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* -------- RECENT BOOKINGS -------- */}
        <div className="bg-white border rounded-xl p-6 shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold text-lg">Recent Bookings</h2>
            <button
              onClick={() => navigate("/admin/bookings")}
              className="text-sm text-exiser-primary font-semibold flex items-center gap-1"
            >
              View All <ArrowRight size={14} />
            </button>
          </div>

          {recentBookings.length === 0 ? (
            <p className="text-gray-500 text-sm">No bookings yet</p>
          ) : (
            <ul className="space-y-3">
              {recentBookings.map((b) => (
                <li
                  key={b.id}
                  className="border p-3 rounded-lg flex justify-between items-center"
                >
                  <div>
                    <p className="font-medium">{b.session_type === 'Identity Transformation Lab' ? 'Growth Mastery' : (b.session_type || "Programme")}</p>
                    <p className="text-xs text-gray-900 font-semibold">{b.email}</p>
                    <p className="text-xs text-gray-500">
                      {b.name} • {new Date(b.created_at || b.booking_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-bold ${b.status === 'CONFIRMED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                      {b.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, onClick }) => (
  <div
    onClick={onClick}
    className="cursor-pointer bg-white border rounded-xl p-6 shadow hover:shadow-lg transition"
  >
    <div className="flex items-center gap-4">
      <div className="p-3 bg-purple-100 text-exiser-primary rounded-lg">
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-xl font-bold">{value}</p>
      </div>
    </div>
  </div>
);

export default AdminDashboard;